import { useFileBrowserStore } from '../stores/fileBrowserStore'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useRegistryStore, persistRegistryExpanded } from '@/features/registry/stores/registryStore'
import { useToastStore } from '@/shared/stores/toastStore'

const PermUpload = 2
const PermDelete = 1
const PermDownload = 4
const PermRename = 16

interface FileToolbarProps {
  onUpload: () => void
  onNewFolder: () => void
  onDeleteSelected: () => void
  onDownloadSelected: () => void
  onRenameSelected: () => void
  onManageGroup?: () => void
  onCreateView?: () => void
  onManageView?: () => void
  onGroupInfo?: () => void
  onViewInfo?: () => void
}

export function FileToolbar({ onUpload, onNewFolder, onDeleteSelected, onDownloadSelected, onRenameSelected, onManageGroup, onCreateView, onManageView, onGroupInfo, onViewInfo }: FileToolbarProps) {
  const { currentPath, currentGroupId, currentGroupPerms, currentGroupRole, currentViewId, currentFolderViewId, checkedItems, refresh } = useFileBrowserStore()
  const canCreateGroup = useAuthStore((s) => s.user?.permissions?.canCreateGroup ?? false)
  const registryConfig = useRegistryStore(s => s.config)
  const registryExpanded = useRegistryStore(s => s.expanded)
  const toggleRegistry = useRegistryStore(s => s.toggleExpanded)
  const logCount = useToastStore(s => s.log.length)
  const toggleLog = useToastStore(s => s.toggleLog)
  const atRoot = currentPath === '/'
  const inGroup = !atRoot
  const canUpload = inGroup && (currentGroupPerms & PermUpload) !== 0
  const canDelete = inGroup && (currentGroupPerms & PermDelete) !== 0
  const canDownload = inGroup && (currentGroupPerms & PermDownload) !== 0
  const canRename = inGroup && (currentGroupPerms & PermRename) !== 0
  const hasDownloadable = canDownload
  const hasChecked = checkedItems.size > 0
  const singleChecked = checkedItems.size === 1
  const isSubfolder = inGroup && currentPath.replace(/^\//, '').replace(/\/$/, '').split('/').length > 1

  return (
    <div className="toolbar" data-testid="toolbar">
      <button
        className="btn-primary"
        onClick={onUpload}
        disabled={!canUpload}
        title={canUpload ? 'Upload files' : 'No upload permission'}
      >
        Upload
      </button>
      <button
        className="btn-secondary"
        onClick={onNewFolder}
        disabled={!canUpload && !atRoot || (atRoot && !canCreateGroup)}
        title={atRoot ? (canCreateGroup ? 'Create new group' : 'No permission to create groups') : canUpload ? 'New folder' : 'No upload permission'}
      >
        {atRoot ? 'Create Group' : 'New Folder'}
      </button>
      {inGroup && registryConfig?.enabled && (
        <button
          className={`btn-secondary${registryExpanded ? ' active' : ''}`}
          onClick={() => { toggleRegistry(); persistRegistryExpanded(currentGroupId, !registryExpanded) }}
          data-testid="registry-toggle-btn"
        >
          {registryConfig.entity_name_plural || 'Records'}
        </button>
      )}
      {hasChecked && (
        <>
          <span className="selection-count">{checkedItems.size} selected</span>
          <button className="btn-success" onClick={onDownloadSelected} disabled={!hasDownloadable}>
            Download
          </button>
          {singleChecked && (
            <button className="btn-warn" onClick={onRenameSelected} disabled={!canRename}>
              Rename
            </button>
          )}
          <button className="btn-danger" onClick={onDeleteSelected} disabled={!canDelete}>
            Delete
          </button>
          <button className="btn-secondary" onClick={() => useFileBrowserStore.getState().clearChecked()}>
            Clear
          </button>
        </>
      )}
      {onManageGroup && inGroup && currentGroupRole === 'admin' && (
        <button className="btn-secondary" onClick={onManageGroup}>Manage Group</button>
      )}
      {onGroupInfo && inGroup && currentGroupRole !== 'admin' && (
        <button className="btn-secondary" onClick={onGroupInfo}>Group Info</button>
      )}
      {onCreateView && inGroup && currentGroupRole === 'admin' && currentViewId === 0 && currentFolderViewId === 0 && isSubfolder && (
        <button className="btn-secondary" onClick={onCreateView}>Create View</button>
      )}
      {onManageView && inGroup && currentGroupRole === 'admin' && (currentViewId > 0 || currentFolderViewId > 0) && (
        <button className="btn-secondary" onClick={onManageView}>Manage View</button>
      )}
      {onViewInfo && inGroup && currentGroupRole !== 'admin' && (currentViewId > 0 || currentFolderViewId > 0) && (
        <button className="btn-secondary" onClick={onViewInfo}>View Info</button>
      )}
      <button className="btn-secondary toolbar-right" onClick={refresh}>
        Refresh
      </button>
      {logCount > 0 && (
        <button className="btn-log" onClick={toggleLog} title="Show message log">
          ⚠ {logCount}
        </button>
      )}
    </div>
  )
}
